import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { theme as antdTheme } from "antd";

const ThemeContext = createContext();

const THEME_KEY = "kubitai-theme-mode";

export const colors = {
  primary: "#00427A",
  secondary: "#00B9F2",
  accent: "#00B9F2",
};

// Keep the historic palette keys exposed by this context so existing
// consumers continue to work while the values follow the Kubit brand.
const LIGHT_COLORS = {
  primary: "#00427A",
  violet: "#00B9F2",
  teal: "#1F7A4D",
  amber: "#8A5A00",
  rose: "#B42318",
};

const DARK_COLORS = {
  primary: "#00427A",
  violet: "#00B9F2",
  teal: "#4FB480",
  amber: "#F0B44C",
  rose: "#F97066",
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved !== null) return saved === "dark";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });

  useEffect(() => {
    localStorage.setItem(THEME_KEY, isDarkMode ? "dark" : "light");
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode((prev) => !prev);

  const palette = isDarkMode ? DARK_COLORS : LIGHT_COLORS;

  const themeConfig = useMemo(
    () => ({
      algorithm: isDarkMode
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: palette.primary,
        colorSuccess: palette.teal,
        colorWarning: palette.amber,
        colorError: palette.rose,
        colorInfo: isDarkMode ? palette.violet : palette.primary,
        colorLink: isDarkMode ? palette.violet : palette.primary,
        colorLinkHover: isDarkMode ? "#4CCAF5" : "#00375F",
        borderRadius: 8,
        borderRadiusLG: 12,
        fontFamily:
          "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontSize: 14,
        colorBgContainer: isDarkMode ? "#171819" : "#FFFFFF",
        colorBgElevated: isDarkMode ? "#242426" : "#FFFFFF",
        colorBgLayout: isDarkMode ? "#090102" : "#F5F8FA",
        colorBorder: isDarkMode ? "#403F41" : "#D6E0E6",
        colorBorderSecondary: isDarkMode ? "#2D2D2F" : "#E5ECF0",
        colorText: isDarkMode ? "#FFFFFF" : "#090102",
        colorTextSecondary: isDarkMode ? "#BBBDC0" : "#403F41",
        colorTextTertiary: isDarkMode ? "#808285" : "#626568",
        colorTextDisabled: "#808285",
        controlHeight: 36,
      },
      components: {
        Layout: {
          siderBg: isDarkMode ? "#171819" : "#FFFFFF",
          headerBg: isDarkMode ? "#171819" : "#FFFFFF",
          bodyBg: isDarkMode ? "#090102" : "#F5F8FA",
        },
        Menu: {
          itemBg: "transparent",
          subMenuItemBg: "transparent",
          itemSelectedBg: isDarkMode
            ? "rgba(0,185,242,0.14)"
            : "#E6F7FD",
          itemSelectedColor: isDarkMode ? palette.violet : palette.primary,
          itemHoverBg: isDarkMode ? "#242426" : "#EDF3F7",
          itemHoverColor: isDarkMode ? "#FFFFFF" : "#090102",
          itemHeight: 36,
          itemBorderRadius: 6,
          itemMarginBlock: 2,
          groupTitleColor: isDarkMode ? "#808285" : "#626568",
        },
        Card: {
          borderRadiusLG: 8,
        },
        Table: {
          borderRadiusLG: 8,
          headerBg: isDarkMode ? "#242426" : "#EDF3F7",
          headerColor: isDarkMode ? "#BBBDC0" : "#403F41",
          rowHoverBg: isDarkMode ? "#242426" : "#F5F8FA",
          cellPaddingBlock: 10,
          cellPaddingInline: 12,
        },
        Button: {
          borderRadius: 8,
          controlHeight: 36,
          controlHeightSM: 32,
          controlHeightLG: 44,
          fontWeight: 500,
          primaryShadow: "none",
        },
        Input: {
          borderRadius: 8,
          controlHeight: 36,
          activeBorderColor: isDarkMode ? "#00B9F2" : "#00427A",
          hoverBorderColor: isDarkMode ? "#00B9F2" : "#00427A",
        },
        Select: {
          borderRadius: 8,
          controlHeight: 36,
          activeBorderColor: isDarkMode ? "#00B9F2" : "#00427A",
          hoverBorderColor: isDarkMode ? "#00B9F2" : "#00427A",
        },
        Modal: {
          borderRadiusLG: 12,
        },
        Tabs: {
          inkBarColor: isDarkMode ? palette.violet : palette.primary,
          itemSelectedColor: isDarkMode ? palette.violet : palette.primary,
          itemHoverColor: isDarkMode ? "#4CCAF5" : "#00375F",
        },
      },
    }),
    [isDarkMode, palette]
  );

  return (
    <ThemeContext.Provider
      value={{ isDarkMode, toggleTheme, themeConfig, colors, palette }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
